import React, { useCallback, useState, useEffect, KeyboardEvent } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import {
    Button,
    GradientDot,
    IconTrqkMark,
    ValidatedInput,
    LoadingEllipses,
    IconInfoCircle,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { constants, getWebpackEnvVar, locale } from '@trqk/utils';
import { validateInputs } from '@trqk/hooks';

import { AuthLayout } from '../../templates';
import { PATHS, URL_PARAMS } from '../../../constants';
import { inputIds } from './constants';

const { CONFIRM_PASSWORD, SIGN_IN_WITH_COGNITO } = queryStrings;

const centerColumnWidth = 350;

/**
 * This is step 2 of resetting a forgotten password; step 1 is in ForgotPassword.
 */
export const ConfirmPasswordReset = (): JSX.Element => {
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const history = useHistory();
    const urlQuery = new URLSearchParams(useLocation().search);
    const initialEmail = urlQuery.get(URL_PARAMS.EMAIL) ?? '';
    const [email, setEmail] = useState(initialEmail);
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);
    const [didSubmit, setDidSubmit] = useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] =
        useState('');

    const [signInWithCognito, signInWithCognitoQuery] = useMutation(
        SIGN_IN_WITH_COGNITO,
        {
            onCompleted: data => {
                const error = data?.signInWithCognito?.error ?? null;

                if (error) {
                    if (
                        error.code ===
                        constants.ERROR_CODES.AWS.NOT_AUTHORIZED_EXCEPTION
                    ) {
                        setConfirmPasswordErrorMessage(
                            locale.enUS.SIGN_IN.ERRORS.BAD_CREDENTIALS,
                        );
                    } else {
                        setConfirmPasswordErrorMessage(error.message);
                    }
                } else {
                    history.push(PATHS.INSIGHTS);
                }
            },
        },
    );

    const [confirmPassword, confirmPasswordQuery] = useMutation(
        CONFIRM_PASSWORD,
        {
            onCompleted: () => {
                signInWithCognito({
                    variables: {
                        username: email,
                        password,
                        clientId,
                        userPoolId,
                        updateUiState: true,
                    },
                });
            },
            onError: (error): void => {
                setConfirmPasswordErrorMessage(error.message);
            },
        },
    );

    const validate = useCallback(
        () =>
            validateInputs([
                {
                    inputId: inputIds.confirmPasswordResetEmail,
                    label: 'Email',
                    type: 'email',
                    value: email,
                    required: true,
                },
                {
                    inputId: inputIds.confirmPasswordResetVerificationCode,
                    label: 'Verification code',
                    type: 'verificationCode',
                    value: code,
                    required: true,
                },
                {
                    inputId: inputIds.confirmPasswordResetPassword,
                    label: 'Password',
                    type: 'password',
                    value: password,
                    required: true,
                },
            ]),
        [code, email, password],
    );

    useEffect(() => {
        if (didSubmit) {
            const validationResults = validate();

            setValidationOutputs(validationResults.validationOutputs);
        }
    }, [didSubmit, validate]);

    const handleSubmit = (): void => {
        setDidSubmit(true);

        const validationResults = validate();

        if (!validationResults.hasValidationErrors) {
            confirmPassword({
                variables: {
                    clientId,
                    newPassword: password,
                    username: email,
                    userPoolId,
                    verificationCode: code,
                },
            });
        } else {
            setValidationOutputs(validationResults.validationOutputs);
        }
    };

    const loading =
        confirmPasswordQuery.loading || signInWithCognitoQuery.loading;

    return (
        <AuthLayout>
            <div className="flex flex-col h-full">
                <div className="flex-grow flex flex-col justify-center items-center">
                    <div style={{ width: centerColumnWidth }}>
                        <div className="flex justify-center">
                            <GradientDot gradientColor="white" width={120}>
                                <IconTrqkMark width={50} />
                            </GradientDot>
                        </div>
                        <div>
                            <h3 className="text-white font-medium text-center my-4">
                                {locale.enUS.CONFIRM_PASSWORD_RESET.TITLE}
                            </h3>
                            <div className="text-center text-white mt-6 mb-4">
                                {locale.enUS.CONFIRM_PASSWORD_RESET.CTA}
                            </div>
                            <div className="my-4" style={{ minHeight: 30 }}>
                                {confirmPasswordErrorMessage && (
                                    <div className="text-center">
                                        <span
                                            className="flex-none"
                                            style={{ height: 20 }}
                                        >
                                            <IconInfoCircle
                                                addClasses="inline"
                                                fillColor="white"
                                            />
                                        </span>
                                        <span className="ml-1 text-sm text-white">
                                            {confirmPasswordErrorMessage}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <ValidatedInput
                                addClasses="my-2"
                                inputId={inputIds.confirmPasswordResetEmail}
                                icon="email"
                                type="email"
                                placeholder="Email"
                                value={email}
                                handleChange={setEmail}
                                padding="large"
                                validationOutputs={validationOutputs}
                            />
                            <ValidatedInput
                                addClasses="my-3"
                                inputId={
                                    inputIds.confirmPasswordResetVerificationCode
                                }
                                icon="password"
                                type="text"
                                value={code}
                                placeholder="Verification Code"
                                handleChange={setCode}
                                padding="large"
                                validationOutputs={validationOutputs}
                            />
                            <ValidatedInput
                                addClasses="my-3"
                                inputId={inputIds.confirmPasswordResetPassword}
                                icon="password"
                                type="password"
                                value={password}
                                placeholder="New Password"
                                handleChange={setPassword}
                                handleBlur={(): void => {
                                    setConfirmPasswordErrorMessage('');
                                }}
                                handleKeyPress={(
                                    e: KeyboardEvent<Element>,
                                ): void => {
                                    if (e.key === 'Enter') {
                                        setConfirmPasswordErrorMessage('');

                                        handleSubmit();
                                    }
                                }}
                                padding="large"
                                validationOutputs={validationOutputs}
                            />
                        </div>
                        <div className="mt-10">
                            <Button
                                color="primary-dark-bg"
                                handleClick={handleSubmit}
                            >
                                <div className="text-center tracking-wider">
                                    {!loading && (
                                        <div>
                                            {
                                                locale.enUS
                                                    .CONFIRM_PASSWORD_RESET
                                                    .BUTTONS.REQUEST_CODE
                                            }
                                        </div>
                                    )}
                                    {loading && <LoadingEllipses width={40} />}
                                </div>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};
